/**
 * 功能：在瀏覽器端以 Canvas API 將任意圖片壓縮至指定大小（Bytes）以內並轉換為 JPEG
 *
 * 策略：
 * 1. 若長寬超過 MAX_DIMENSION，等比縮放至該上限以降低像素數
 * 2. 以二分搜尋法找出最高品質且仍符合大小限制的 JPEG quality 值（0.1 ~ 1.0）
 * 3. 傳回新的 File 物件，名稱強制為 .jpg，type 為 image/jpeg
 *
 * @param {File} file 原始圖片檔案（支援 JPEG、PNG、WebP 等瀏覽器可解碼格式）
 * @param {number} maxBytes 壓縮目標上限（預設 1 MB = 1_048_576 bytes）
 * @returns {Promise<File>} 壓縮後的 JPEG File 物件
 */
export async function compressImageToJpeg(file: File, maxBytes = 409_600): Promise<File> {
  const MAX_DIMENSION = 2000; // px，超過此尺寸先等比縮放
  const MIN_QUALITY = 0.1;

  // 1. 將 File 解碼為 HTMLImageElement
  const bitmap = await createImageBitmap(file);

  // 2. 計算縮放後尺寸
  let { width, height } = bitmap;
  if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
    const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  // 3. 繪製到 OffscreenCanvas（或降級至 HTMLCanvasElement）
  const canvas = typeof OffscreenCanvas !== 'undefined'
    ? new OffscreenCanvas(width, height)
    : (() => {
        const c = document.createElement('canvas');
        c.width = width;
        c.height = height;
        return c;
      })();

  const ctx = (canvas as HTMLCanvasElement).getContext('2d') ??
    (canvas as OffscreenCanvas).getContext('2d');
  if (!ctx) throw new Error('無法建立 Canvas 2D 繪製環境');
  (ctx as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D)
    .drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  /**
   * 將 canvas 以指定 quality 轉出 Blob
   * @param {number} quality JPEG 品質 0~1
   */
  async function toBlob(quality: number): Promise<Blob> {
    if (canvas instanceof OffscreenCanvas) {
      return (canvas as OffscreenCanvas).convertToBlob({ type: 'image/jpeg', quality });
    }
    return new Promise<Blob>((resolve, reject) => {
      (canvas as HTMLCanvasElement).toBlob(
        (b) => (b ? resolve(b) : reject(new Error('toBlob 回傳 null'))),
        'image/jpeg',
        quality,
      );
    });
  }

  // 4. 若最大品質已符合限制，直接傳回（避免不必要的品質損失）
  const fullBlob = await toBlob(1.0);
  if (fullBlob.size <= maxBytes) {
    return blobToFile(fullBlob, file.name);
  }

  // 5. 二分搜尋最佳 quality
  let lo = MIN_QUALITY;
  let hi = 1.0;
  let bestBlob: Blob = fullBlob;

  for (let i = 0; i < 8; i++) {
    const mid = (lo + hi) / 2;
    const blob = await toBlob(mid);
    if (blob.size <= maxBytes) {
      bestBlob = blob;
      lo = mid;
    } else {
      hi = mid;
    }
  }

  // 6. 保底：若最低品質仍超標，強制使用 MIN_QUALITY 結果
  if (bestBlob.size > maxBytes) {
    bestBlob = await toBlob(MIN_QUALITY);
  }

  return blobToFile(bestBlob, file.name);
}

/**
 * 功能：將 Blob 包裝為 File 物件，並強制使用 .jpg 副檔名
 * @param {Blob} blob JPEG Blob
 * @param {string} originalName 原始檔案名稱（用於保留主檔名）
 * @returns {File} 正確命名的 JPEG File
 */
function blobToFile(blob: Blob, originalName: string): File {
  const base = originalName.replace(/\.[^.]+$/, '');
  return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
}
